import PropTypes from 'prop-types';
// form
import { useFormContext, Controller } from 'react-hook-form';
// @mui
import { FormHelperText, Box, IconButton } from '@mui/material';
import Iconify from '../Iconify';
// type
import { UploadAvatar, UploadMultiFile, UploadSingleFile } from '../upload';

// ----------------------------------------------------------------------

RHFUploadAvatar.propTypes = {
  name: PropTypes.string,
};

export function RHFUploadAvatar({ name, ...other }) {
  const { control } = useFormContext();

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState: { error } }) => {
        const checkError = !!error && !field.value;

        return (
          <div>
            <UploadAvatar error={checkError} {...other} file={field.value} />
            {checkError && (
              <FormHelperText error sx={{ px: 2, textAlign: 'center' }}>
                {error.message}
              </FormHelperText>
            )}
          </div>
        );
      }}
    />
  );
}

// ----------------------------------------------------------------------

RHFUploadSingleFile.propTypes = {
  name: PropTypes.string,
};

export function RHFUploadSingleFile({ name, ...other }) {
  const { control } = useFormContext();

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState: { error } }) => {
        const checkError = !!error && !field.value;
        return (
          <Box sx={{ position: 'relative', width: '100%' }}>
            <UploadSingleFile
              accept="image/*"
              file={field.value}
              error={checkError}
              helperText={
                checkError && (
                  <FormHelperText error sx={{ px: 2 }}>
                    {error.message}
                  </FormHelperText>
                )
              }
              {...other}
            />
            {((typeof field.value === 'string' && field.value !== '') || (typeof field.value === 'object' && field.value !== null && !Array.isArray(field.value))) && (
              <IconButton
                color="error"
                size="medium"
                sx={{ position: 'absolute', top: 8, right: 8, zIndex: 2 }}
                onClick={() => field.onChange(null)}
              >
                <Iconify icon="eva:trash-2-outline" width={40} height={40} />
              </IconButton>
            )}
          </Box>
        );
      }}
    />
  );
}

// ----------------------------------------------------------------------

RHFUploadMultiFile.propTypes = {
  name: PropTypes.string,
  onMove: PropTypes.func,
};

// Images can be reordered by default: the field's array is rearranged and the
// order is what the form submits. Pass onMove to handle it yourself (a form
// that keeps the images in its own state too), or onMove={null} to turn it off.
export const moveItem = (list, from, to) => {
  const next = [...(list || [])];
  const [item] = next.splice(from, 1);
  next.splice(to, 0, item);
  return next;
};

export function RHFUploadMultiFile({ name, onMove, ...other }) {
  const { control } = useFormContext();

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState: { error } }) => {
        const checkError = !!error && field.value?.length === 0;
        const handleMove =
          onMove === undefined ? (from, to) => field.onChange(moveItem(field.value, from, to)) : onMove;

        return (
          <UploadMultiFile
            accept="image/*"
            files={field.value}
            error={checkError}
            onMove={handleMove || undefined}
            helperText={
              checkError && (
                <FormHelperText error sx={{ px: 2 }}>
                  {error?.message}
                </FormHelperText>
              )
            }
            {...other}
          />
        );
      }}
    />
  );
}
